import { z } from 'zod';

/**
 * Whiteboard and bulletin board contracts (spec §6.15). Both are interactive objects (ADR-0026):
 * the player presses E at the board, the server replies with its content, and every change is an
 * `interact` with an action and data, so range, rate limit and permission checks are shared.
 */

// --- Whiteboard -------------------------------------------------------------------------------

/** Board space in pixels; the client scales it to the panel. */
export const WHITEBOARD_WIDTH = 640;
export const WHITEBOARD_HEIGHT = 400;
/** The 8-colour palette (spec §6.15). */
export const WHITEBOARD_COLORS = [
  '#1f2937',
  '#dc2626',
  '#ea580c',
  '#ca8a04',
  '#16a34a',
  '#2563eb',
  '#7c3aed',
  '#f8fafc',
] as const;
/** Pen widths in board pixels. */
export const WHITEBOARD_WIDTHS = [2, 4, 8] as const;
/**
 * Points per stroke message. Points are half pixels, so `[x0, y0, x1, y1, …]` stays within the
 * 1 KB `interact` payload; longer lines are sent as several segments of one `group`.
 */
export const WHITEBOARD_SEGMENT_POINTS = 64;
/** Spec cap of 500 points per stroke, across the segments of a group. */
export const WHITEBOARD_GROUP_POINTS = 500;
/** Stored segments per board (spec: 5,000 strokes). */
export const WHITEBOARD_MAX_STROKES = 5_000;

const halfPixel = (max: number) =>
  z
    .number()
    .int()
    .min(0)
    .max(max * 2);

/** A client-chosen id that ties the segments of one pen stroke together (undo removes them all). */
export const strokeGroupSchema = z
  .string()
  .min(1)
  .max(16)
  .regex(/^[A-Za-z0-9_-]+$/);

/** `draw`: one segment of a pen stroke. Coordinates are in half pixels of board space. */
export const whiteboardStrokeInputSchema = z
  .object({
    group: strokeGroupSchema,
    color: z.enum(WHITEBOARD_COLORS),
    width: z.union([z.literal(2), z.literal(4), z.literal(8)]),
    points: z
      .array(z.number().int())
      .min(2)
      .max(WHITEBOARD_SEGMENT_POINTS * 2)
      .refine((p) => p.length % 2 === 0, { message: 'points come in pairs' })
      .refine(
        (p) =>
          p.every((v, i) =>
            i % 2 === 0
              ? halfPixel(WHITEBOARD_WIDTH).safeParse(v).success
              : halfPixel(WHITEBOARD_HEIGHT).safeParse(v).success,
          ),
        { message: 'point outside the board' },
      ),
  })
  .strict();
export type WhiteboardStrokeInput = z.infer<typeof whiteboardStrokeInputSchema>;

/** `erase`: removes strokes by id (own strokes, or any stroke for admins). */
export const whiteboardEraseInputSchema = z
  .object({ ids: z.array(z.string().uuid()).min(1).max(20) })
  .strict();

/** A stored stroke segment as clients draw it. */
export interface WhiteboardStroke {
  readonly id: string;
  readonly group: string;
  readonly authorId: string;
  readonly color: string;
  readonly width: number;
  /** Half pixels, `[x0, y0, x1, y1, …]`. */
  readonly points: readonly number[];
}

/** `open` reply: the whole board, and whether this player may clear it. */
export interface WhiteboardSnapshot {
  readonly strokes: readonly WhiteboardStroke[];
  readonly canClear: boolean;
}

// --- Bulletin board ---------------------------------------------------------------------------

export const BULLETIN_WIDTH = 640;
export const BULLETIN_HEIGHT = 400;
export const BULLETIN_NOTE_MAX_CHARS = 280;
export const BULLETIN_MAX_NOTES = 100;
export const BULLETIN_NOTE_COLORS = ['yellow', 'pink', 'blue', 'green', 'orange'] as const;
export type BulletinNoteColor = (typeof BULLETIN_NOTE_COLORS)[number];
/** Days a note stays up; the default is 30 (spec §6.15). */
export const BULLETIN_EXPIRY_DAYS = [1, 7, 30] as const;
export const BULLETIN_DEFAULT_EXPIRY_DAYS = 30;

/** `post`: a new sticky note at a position on the board. */
export const bulletinNoteInputSchema = z
  .object({
    text: z
      .string()
      .transform((s) => s.trim())
      .pipe(z.string().min(1).max(BULLETIN_NOTE_MAX_CHARS)),
    color: z.enum(BULLETIN_NOTE_COLORS),
    x: z.number().int().min(0).max(BULLETIN_WIDTH),
    y: z.number().int().min(0).max(BULLETIN_HEIGHT),
    expiresInDays: z
      .union([z.literal(1), z.literal(7), z.literal(30)])
      .default(BULLETIN_DEFAULT_EXPIRY_DAYS),
  })
  .strict();
export type BulletinNoteInput = z.input<typeof bulletinNoteInputSchema>;

/** `remove`: takes a note down (its author or an admin). */
export const bulletinRemoveInputSchema = z.object({ id: z.string().uuid() }).strict();

export interface BulletinNote {
  readonly id: string;
  readonly authorId: string;
  readonly authorName: string;
  readonly text: string;
  readonly color: BulletinNoteColor;
  readonly x: number;
  readonly y: number;
  /** ISO timestamps. */
  readonly createdAt: string;
  readonly expiresAt: string;
}

/** `open` reply: the notes still up, oldest first. */
export interface BulletinSnapshot {
  readonly notes: readonly BulletinNote[];
}
